/**
 * GRIDHEIMR Register Page
 *
 * Registration form with GitHub OAuth option and email/password fallback.
 * Matches the Swedish Tron aesthetic of Login.tsx.
 */

import React, { useState, FormEvent, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { register } from "../api/auth";
import { TronGrid } from "../components/TronGrid";
import { motion } from "framer-motion";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO, FONT_URL } from "../theme";

const GITHUB_AUTH_URL = "https://app.gridheimr.com/auth/github";

export function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!document.getElementById("reg-fonts")) {
      const l = Object.assign(document.createElement("link"), {
        id: "reg-fonts", rel: "stylesheet", href: FONT_URL,
      });
      document.head.appendChild(l);
    }
    if (!document.getElementById("reg-css")) {
      const s = Object.assign(document.createElement("style"), { id: "reg-css" });
      s.textContent = `
        .gh-reg-github:hover {
          transform: translate(-2px, -2px);
          box-shadow: 0 0 20px ${YELLOW}, 0 0 40px rgba(254,204,0,0.3), 4px 4px 0 ${BLUE} !important;
        }
        .gh-reg-toggle:hover { color: ${YELLOW} !important; }
        .gh-reg-input:focus {
          border-color: ${BLUE} !important;
          box-shadow: 0 0 12px rgba(0,106,167,0.4) !important;
          outline: none;
        }
        .gh-reg-submit:hover {
          background: ${BLUE} !important;
          box-shadow: 0 0 16px rgba(0,106,167,0.5) !important;
        }
        .gh-reg-link:hover { color: ${YELLOW} !important; }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await register({ email, password, full_name: fullName });
      navigate("/login", { state: { message: "Registration successful! Please log in." } });
    } catch (err: any) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <TronGrid />

      <motion.div style={S.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}>

        {/* Logo */}
        <motion.div style={S.logo}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}>
          <span style={S.logoBlue}>GRID</span>
          <span style={S.logoYellow}>HEIMR</span>
        </motion.div>

        <p style={S.tagline}>⚔️ Enter the Grid</p>

        {/* GitHub OAuth — primary CTA */}
        <motion.a
          href={GITHUB_AUTH_URL}
          className="gh-reg-github"
          style={S.githubBtn}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}>
          <svg style={S.githubIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
          </svg>
          JOIN WITH GITHUB
        </motion.a>

        {/* Divider */}
        <div style={S.divider}>
          <div style={S.dividerLine} />
          <span style={S.dividerText}>or</span>
          <div style={S.dividerLine} />
        </div>

        {/* Toggle form */}
        {!showForm ? (
          <button
            className="gh-reg-toggle"
            style={S.toggleBtn}
            onClick={() => setShowForm(true)}>
            register with email / password ▾
          </button>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            style={S.form}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}>

            {error && (
              <div style={S.errorBox}>
                <span style={S.errorText}>{error}</span>
              </div>
            )}

            <div style={S.inputGroup}>
              <label style={S.label}>FULL NAME</label>
              <input
                className="gh-reg-input"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={isLoading}
                style={S.input}
                autoComplete="name"
                placeholder="Viktor Eriksson"
              />
            </div>

            <div style={S.inputGroup}>
              <label style={S.label}>EMAIL</label>
              <input
                className="gh-reg-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                style={S.input}
                autoComplete="email"
                placeholder="you@gridheimr.com"
              />
            </div>

            <div style={S.inputGroup}>
              <label style={S.label}>PASSWORD</label>
              <input
                className="gh-reg-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                disabled={isLoading}
                style={S.input}
                autoComplete="new-password"
                placeholder="min. 8 characters"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="gh-reg-submit"
              style={S.submitBtn}>
              {isLoading ? "FORGING IDENTITY..." : "⚔️ FORGE MY IDENTITY"}
            </button>
          </motion.form>
        )}

        <p style={S.footer}>
          Already on the grid?{" "}
          <Link to="/login" className="gh-reg-link" style={S.footerLink}>
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: DARK_BG,
    overflow: "hidden",
  },
  card: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "420px",
    margin: "0 1rem",
    padding: "2.5rem 2rem",
    background: "rgba(0,10,18,0.85)",
    border: `1px solid rgba(0,106,167,0.3)`,
    boxShadow: `0 0 40px rgba(0,106,167,0.1), inset 0 0 40px rgba(0,106,167,0.03)`,
    backdropFilter: "blur(8px)",
  },
  logo: {
    fontFamily: PIXEL,
    fontSize: "clamp(1.4rem, 5vw, 2rem)",
    fontWeight: 400,
    textAlign: "center",
    marginBottom: "0.5rem",
    letterSpacing: "0.06em",
  },
  logoBlue: {
    color: BLUE,
    textShadow: `0 0 20px rgba(0,106,167,0.7), 0 0 50px rgba(0,106,167,0.3)`,
  },
  logoYellow: {
    color: YELLOW,
    textShadow: `0 0 20px rgba(254,204,0,0.6), 0 0 50px rgba(254,204,0,0.2)`,
  },
  tagline: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
    textAlign: "center",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    marginBottom: "2rem",
    marginTop: 0,
  },
  githubBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    width: "100%",
    padding: "14px 24px",
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: "#000",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    letterSpacing: "0.1em",
    textDecoration: "none",
    transition: "all 0.15s ease",
    boxShadow: `0 0 12px rgba(254,204,0,0.35), 3px 3px 0 ${BLUE}`,
  },
  githubIcon: { width: "18px", height: "18px", flexShrink: 0 },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    margin: "1.5rem 0",
  },
  dividerLine: { flex: 1, height: "1px", background: "rgba(0,106,167,0.2)" },
  dividerText: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.1em",
  },
  toggleBtn: {
    width: "100%",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.05em",
    padding: "0.5rem 0",
    transition: "color 0.2s",
    textAlign: "center",
  },
  form: { display: "flex", flexDirection: "column", gap: "1rem", overflow: "hidden" },
  errorBox: {
    background: "rgba(255,68,68,0.12)",
    border: "1px solid rgba(255,68,68,0.4)",
    padding: "0.75rem 1rem",
  },
  errorText: { fontFamily: MONO, fontSize: "0.8rem", color: "#ff6b6b" },
  inputGroup: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  label: {
    fontFamily: PIXEL,
    fontSize: "0.4rem",
    color: YELLOW,
    letterSpacing: "0.15em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  input: {
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "#fff",
    background: "rgba(0,106,167,0.06)",
    border: `1px solid rgba(0,106,167,0.25)`,
    padding: "10px 14px",
    transition: "all 0.2s ease",
    outline: "none",
  },
  submitBtn: {
    fontFamily: PIXEL,
    fontSize: "0.45rem",
    fontWeight: 400,
    padding: "14px",
    background: "transparent",
    color: BLUE,
    border: `2px solid ${BLUE}`,
    cursor: "pointer",
    letterSpacing: "0.12em",
    transition: "all 0.2s ease",
    boxShadow: `0 0 8px rgba(0,106,167,0.2)`,
    marginTop: "0.25rem",
  },
  footer: {
    marginTop: "1.75rem",
    textAlign: "center",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
  },
  footerLink: {
    color: BLUE,
    textDecoration: "none",
    transition: "color 0.2s",
    letterSpacing: "0.05em",
  },
};

